import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import { AppointmentChangeOrigin, AppointmentChangeRecordData, AppointmentChangeType } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { Card, CardContent, Chip, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EventIcon from '@material-ui/icons/Event';
import PersonIcon from '@material-ui/icons/Person';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { toDateOnlyString } from '../../shared/utils';
import AppButton from '../AppButton';

export interface AppointmentsChangesProps {
    clientId: string;
    onLoadData: (pageSize: number, offset: number) => Promise<AppointmentChangeRecordData[]>;
}

const AppointmentsChanges: React.FC<AppointmentsChangesProps> = ({ clientId, onLoadData }) => {
    const t = useTranslations();
    const [changes, setChanges] = useState<AppointmentChangeRecordData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const pageSize = 50;

    const loadChanges = useCallback(async (newOffset: number) => {
        if (isLoading) return;

        setIsLoading(true);
        try {
            const data = await onLoadData(pageSize, newOffset);

            if (newOffset === 0) {
                setChanges(data);
            } else {
                setChanges(prev => [...prev, ...data]);
            }

            setHasMore(data.length === pageSize);
            setOffset(newOffset);
        } catch (error) {
            console.error('Error loading appointment changes:', error);
        } finally {
            setIsLoading(false);
        }
    }, [clientId, onLoadData, isLoading, pageSize]);

    useEffect(() => {
        loadChanges(0);
    }, []);

    const handleLoadMore = useCallback(() => {
        loadChanges(offset + pageSize);
    }, [offset, pageSize, loadChanges]);

    const formatChangeType = (changeType: AppointmentChangeType): string => {
        switch (changeType) {
            case AppointmentChangeType.Deleted: return t(TK.cancelled);
            case AppointmentChangeType.DateChanged: return t(TK.dateChanged);
            case AppointmentChangeType.ClientChanged: return t(TK.clientChanged);
            default: return '';
        }
    };

    const formatChangeOrigin = (origin: AppointmentChangeOrigin): string => ({
        [AppointmentChangeOrigin.None]: t(TK.none),
        [AppointmentChangeOrigin.Professional]: t(TK.professional),
        [AppointmentChangeOrigin.Client]: t(TK.client),
        [AppointmentChangeOrigin.Organization]: t(TK.organization),
        [AppointmentChangeOrigin.Other]: t(TK.other),
    }[origin] || '');

    const formatDateTime = (value: unknown): string => {
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? '-' : value.toLocaleString('pt-PT');
        }
        if (typeof value === 'string' && value.trim().length) {
            const date = new Date(value);
            return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-PT');
        }
        return '-';
    };

    const formatDateOnly = (value: unknown): string => toDateOnlyString(value) ?? '-';

    const getChangeTypeColor = (changeType: AppointmentChangeType): 'default' | 'primary' | 'secondary' => {
        switch (changeType) {
            case AppointmentChangeType.ClientChanged: 
            case AppointmentChangeType.Deleted: return 'secondary'; 
            case AppointmentChangeType.DateChanged: return 'primary'; 
            default: return 'default';
        }
    };

    const getChangeTypeIcon = (changeType: AppointmentChangeType) => {
        switch (changeType) {
            case AppointmentChangeType.Deleted: return <DeleteIcon style={{ fontSize: 16 }} />;
            case AppointmentChangeType.DateChanged: return <EventIcon style={{ fontSize: 16 }} />;
            case AppointmentChangeType.ClientChanged: return <PersonIcon style={{ fontSize: 16 }} />;
            default: return null;
        }
    };

    const containerStyle: React.CSSProperties = {
        padding: 'var(--spacer-M)',
    };

    const cardStyle: React.CSSProperties = {
        marginBottom: 'var(--spacer-S)',
        borderLeft: `var(--border-thick) solid var(--appBlue)`,
    };

    const cardHeaderStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--spacer-XS)',
    };

    const cardBodyStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 'var(--spacer-S)',
        marginTop: 'var(--spacer-S)',
    };

    const infoItemStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacer-XXS)',
    };

    const labelStyle: React.CSSProperties = {
        fontSize: '0.75rem',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        fontWeight: 600,
    };

    const valueStyle: React.CSSProperties = {
        fontSize: '0.875rem',
        color: 'var(--text-body)',
    };

    const timestampStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacer-XXS)',
        color: 'var(--text-muted)',
        fontSize: '0.875rem',
    };

    const centerStyle: React.CSSProperties = {
        textAlign: 'center',
        marginTop: 'var(--spacer-M)',
        marginBottom: 'var(--spacer-M)',
    };

    return (
        <div style={containerStyle}>
            {changes.map((change) => (
                <Card key={change.id} style={cardStyle} elevation={1}>
                    <CardContent>
                        <div style={cardHeaderStyle}>
                            <div>
                                <Chip
                                    icon={getChangeTypeIcon(change.changeType) || undefined}
                                    label={formatChangeType(change.changeType)}
                                    color={getChangeTypeColor(change.changeType)}
                                    size="small"
                                />
                            </div>
                            <div style={timestampStyle}>
                                <AccessTimeIcon style={{ fontSize: 16 }} />
                                {formatDateTime(change.timestamp)} - {change.username}
                            </div>
                        </div>

                        <div style={cardBodyStyle}>
                            <div style={infoItemStyle}>
                                <span style={labelStyle}>{t(TK.appointment)}</span>
                                <span style={valueStyle}>
                                    <div>{formatDateOnly(change.appointmentDate)}</div>
                                    <div>{change.appointmentType} {t(TK.with)} {change.professionalName || '-'}</div>
                                </span>
                            </div>

                            <div style={infoItemStyle}>
                                <span style={labelStyle}>{t(TK.changeOrigin)}</span>
                                <span style={valueStyle}>{formatChangeOrigin(change.reasonOrigin)}</span>
                            </div>

                            {change.reason && (
                                <div style={infoItemStyle}>
                                    <span style={labelStyle}>{t(TK.reason)}</span>
                                    <span style={valueStyle}>{change.reason}</span>
                                    {change.reasonDetails && (
                                        <span style={{ ...valueStyle, fontSize: '0.75rem', fontStyle: 'italic' }}>
                                            {change.reasonDetails}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {(change.changeType === 1 && change.newDate) && (
                            <div style={{ marginTop: 'var(--spacer-S)', paddingTop: 'var(--spacer-S)', borderTop: `var(--border-thin) solid var(--appLigthGrey)` }}>
                                <div style={infoItemStyle}>
                                    <span style={labelStyle}>{t(TK.newDate)}</span>
                                    <span style={valueStyle}>{formatDateOnly(change.newDate)}</span>
                                </div>
                            </div>
                        )}

                        {(change.changeType === 2 && change.newClientName) && (
                            <div style={{ marginTop: 'var(--spacer-S)', paddingTop: 'var(--spacer-S)', borderTop: `var(--border-thin) solid var(--appLigthGrey)` }}>
                                <div style={infoItemStyle}>
                                    <span style={labelStyle}>{t(TK.newClient)}</span>
                                    <span style={valueStyle}>{change.newClientName}</span>
                                </div>
                            </div>
                        )}

                    </CardContent>
                </Card>
            ))}

            {isLoading && (
                <div style={centerStyle}>
                    <Spinner color="primary" />
                </div>
            )}

            {!isLoading && changes.length === 0 && (
                <div style={centerStyle}>
                    <Typography variant="body1" color="textSecondary">
                        {t(TK.empty)}
                    </Typography>
                </div>
            )}

            {!isLoading && hasMore && changes.length > 0 && (
                <div style={centerStyle}>
                    <AppButton color="primary" onClick={handleLoadMore}>
                        {t(TK.loadMore)}
                    </AppButton>
                </div>
            )}
        </div>
    );
};

export default AppointmentsChanges;
